namespace Solovis.Reports.Parameters.Stats {

  export class OtherOptionsOptions {
    configurations?: OtherOptionsConfigurations;
    columns: OtherOptionsColumnOptions = new OtherOptionsColumnOptions();
  }

  type OptionName = Solovis.UI.InputControls.Paths<OtherOptionsOptions>;

  const getOtherOptionsFormEntries = (options: OtherOptionsOptions): Record<OptionName, UI.InputControls.FormEntryOptions<OtherOptionsOptions>> => ({
    "configurations": {
      name: "configurations",
      label: "Additional Configurations",
      isGroupHeader: true
    },
    "configurations.bookValues": {
      name: "configurations.bookValues",
      label: "Book Values",
      help: Solovis.Reports.Parameters.SourceDataHelpText,
      initialValue: options.configurations?.bookValues,
      editor: { type: "bool" },
    },
    "configurations.blendedValues": {
      name: "configurations.blendedValues",
      label: "Use Blended Values",
      help: { html: Solovis.Reports.Parameters.BlendedValuesHelpText },
      initialValue: options.configurations?.blendedValues,
      editor: { type: "bool" },
      isVisible: (formEntry) => {
        return formEntry.formBuilder.getValueByName("configurations.bookValues");
      }
    },
    "configurations.groupByCommonHoldings": {
      name: "configurations.groupByCommonHoldings",
      label: "Group By Common Holdings",
      help: Solovis.Reports.Parameters.Composites.GroupByCommonHoldingsHelpText,
      initialValue: options.configurations?.groupByCommonHoldings,
      editor: { type: "bool" },
    },
    "configurations.allLabel": {
      name: "configurations.allLabel",
      label: "'All' Label",
      help: Solovis.Reports.Parameters.Composites.AllLabelOverrideHelpText,
      initialValue: options.configurations?.allLabel,
      editor: { type: "text" },
    },
    "configurations.removeAllLine": {
      name: "configurations.removeAllLine",
      label: "Remove All Line",
      help: "Removes the all line from the report.",
      initialValue: options.configurations?.removeAllLine,
      editor: { type: "bool" },
    },
    "configurations.createOtherGroups": {
      name: "configurations.createOtherGroups",
      label: "Create Other Groups",
      help: Solovis.Reports.Parameters.Composites.CreateOtherGroupsHelpText,
      initialValue: options.configurations?.createOtherGroups,
      editor: { type: "bool" },
    },
    "configurations.removeEmptyComponents": {
      name: "configurations.removeEmptyComponents",
      label: "Remove Empty Components",
      help: { html: Solovis.Reports.Parameters.Composites.RemoveEmptyComponentsHelpHtml },
      initialValue: options.configurations?.removeEmptyComponents,
      editor: { type: "bool" },
    },
    "configurations.includeZeros": {
      name: "configurations.includeZeros",
      label: "Include Zeros",
      initialValue: options.configurations?.includeZeros,
      editor: { type: "bool" },
      isVisible: (formEntry) => {
        return formEntry.formBuilder.getValueByName("configurations.removeEmptyComponents");
      }
    },
    "configurations.hideCharts": {
      name: "configurations.hideCharts",
      label: "Hide Charts",
      help: "Hides the chart in the report.",
      initialValue: options.configurations?.hideCharts,
      editor: { type: "bool" },
    },
    "configurations.cumulativeIrr": {
      name: "configurations.cumulativeIrr",
      label: "Cumulative IRR",
      initialValue: options.configurations?.cumulativeIrr,
      editor: { type: "bool" },
    },
    "configurations.eodIrr": {
      name: "configurations.eodIrr",
      label: "EoD IRR",
      help: { url: "~/Help/InvestmentModelHelp/CalculationsReference#eod_cumulative_irr" },
      initialValue: options.configurations?.eodIrr,
      editor: { type: "bool" },
    },
    "configurations.twrSettingsSourceAsset": {
      name: "configurations.twrSettingsSourceAsset",
      label: "TWR Settings Source Asset",
      help: { html: Solovis.Reports.Parameters.Composites.SourceSettingsAssetHelpHtml },
      initialValue: options.configurations?.twrSettingsSourceAsset,
      editor: {
        type: "parameter",
        parameterOptions: {
          parameterType: "entityOrInvestment"
        }
      }
    },
    "columns": {
      name: "columns",
      label: "Additional Columns",
      isGroupHeader: true
    },
    "columns.localCurrencyCode": {
      name: "columns.localCurrencyCode",
      label: "Local Currency Code",
      initialValue: options.columns?.localCurrencyCode,
      editor: { type: "bool" },
    },
    "columns.currencyCode": {
      name: "columns.currencyCode",
      label: "Currency Code",
      initialValue: options.columns?.currencyCode,
      editor: { type: "bool" },
    },
    "columns.fullLabelPath": {
      name: "columns.fullLabelPath",
      label: "Full Label Path",
      initialValue: options.columns?.fullLabelPath,
      editor: { type: "bool" },
    },
    "columns.isGroup": {
      name: "columns.isGroup",
      label: "Is Group",
      initialValue: options.columns?.isGroup,
      editor: { type: "bool" },
    },
    "columns.compositeTagId": {
      name: "columns.compositeTagId",
      label: "Composite Tag Id",
      initialValue: options.columns?.compositeTagId,
      editor: { type: "bool" },
    },
    "columns.compositeTagCode": {
      name: "columns.compositeTagCode",
      label: "Composite Tag Code",
      initialValue: options.columns?.compositeTagCode,
      editor: { type: "bool" },
    },
    "columns.groupNodeId": {
      name: "columns.groupNodeId",
      label: "Group Node Id",
      initialValue: options.columns?.groupNodeId,
      editor: { type: "bool" },
    },
    "columns.parentGroupNodeId": {
      name: "columns.parentGroupNodeId",
      label: "Parent Group Node Id",
      initialValue: options.columns?.parentGroupNodeId,
      editor: { type: "bool" },
    },
  })

  export interface OtherOptionsConfigurations {
    allLabel: string;
    bookValues: boolean;
    blendedValues: boolean;
    createOtherGroups: boolean;
    cumulativeIrr: boolean;
    eodIrr: boolean;
    groupByCommonHoldings: boolean;
    hideCharts: boolean;
    includeZeros: boolean;
    removeAllLine: boolean;
    removeEmptyComponents: boolean;
    twrSettingsSourceAsset: any;
  }

  export class OtherOptionsColumnOptions
  {
    localCurrencyCode: boolean = false;
    currencyCode: boolean = false;
    fullLabelPath: boolean = false;
    isGroup: boolean = false;
    compositeTagId: boolean = false;
    compositeTagCode: boolean = false;
    groupNodeId: boolean = false;
    parentGroupNodeId: boolean = false;
  }

  export class OtherOptionsParameterControl extends Solovis.UI.BaseControl {
    private _options: OtherOptionsOptions;

    private _optionsInUse: Array<OptionName>;

    constructor(options: Solovis.UI.BaseControlOptions & {
      initialValue?: OtherOptionsOptions;
      settings?: { optionsInUse?: Array<OptionName> };
    }) {
      super(options);
      this._options = options.initialValue ?? null;
      this._optionsInUse = options?.settings?.optionsInUse;
    }

    public async doRender() {
      let self = this;
      let label = $("<a>").text("Other Options").appendTo(this.container);
      label.click(function () {
        self._showEditor();
      });
      let editLink = $("<a>").appendTo(this.container)
        .css("padding-left", "5px")
        .html("<span class='fa fa-pencil'></span>");
      editLink.click(function () {
        self._showEditor();
      });
    }

    private _showEditor() {
      const editorDialog = new AdvancedOptionsEditorDialog({
        options: this._options,
        optionsInUse: this._optionsInUse,
        saveCallback: (data: OtherOptionsOptions) => this.setParameterValue(data),
      });
      editorDialog.show();
    }

    public _setCurrentSourceDataOptions(data) {
      this._options = data ?? new OtherOptionsOptions();
      this._options.columns ??= new OtherOptionsColumnOptions();
    }

    public setParameterValue(newValue, more?) {
      Solovis.Reports.Parameters.ParameterControlHelper.handleSetParameterValue(
        this, OtherOptionsParameterControl, this._setCurrentSourceDataOptions, newValue, more);
    }

    public getParameterValue() { return Solovis.deepCopy(this._options); }

    public notifyParameterValueChanged() { $(this).trigger("parameterValueChanged"); }
  }


  export class AdvancedOptionsEditorDialog {

    private _opts: OtherOptionsOptions;
    private _saveCallback;
    private formBuilder: Solovis.UI.InputControls.FormBuilder<OtherOptionsOptions>;
    private _optionsInUse?: Array<OptionName>;

    public constructor(options: { options: OtherOptionsOptions, optionsInUse?: Array<OptionName>, saveCallback: any; }) {
      this._opts = options.options ? options.options : new OtherOptionsOptions();
      this._saveCallback = options.saveCallback;
      this._optionsInUse = options.optionsInUse;
    }

    public show() {
      const options = this._opts;
      this.formBuilder = new Solovis.UI.InputControls.FormBuilder({
        container: $("<div>"),
        width: 700,
        labelColumnWidth: 220
      });

      const optionOptionsFormEntires = getOtherOptionsFormEntries(options);
      const getFormBuilderEntry = (name: OptionName): UI.InputControls.FormEntryOptions<OtherOptionsOptions> => {
        return optionOptionsFormEntires[name];
      }

      // Check which options should be included in the form
      for (const optionName of Object.keys(optionOptionsFormEntires) as Array<OptionName>) {
        if(!this.isOptionInUse(optionName)) {
          continue;
        }
        const entry = getFormBuilderEntry(optionName)
        this.formBuilder.addEntry(entry);
      }

      this.formBuilder.setupDone();
      this.formBuilder.showAsDialog({
        title: "Edit Other Options",
        desiredWidth: 720,
        desiredHeight: 800,
        showSaveCancel: true,
        saveButtonText: "OK",
        saveCallback: () => {
          const newOptions = this.formBuilder.getValues();
          if (newOptions.configurations && !newOptions.configurations.bookValues) {
            newOptions.configurations.blendedValues = false;
          }
          this._saveCallback(newOptions);
          return true;
        }
      });
    }

    private isOptionInUse (optionName: OptionName) {
      if (!this._optionsInUse) {
        return true;
      }
      return this._optionsInUse.some((option) => option === optionName || option.startsWith(optionName + "."));
    }
  }
}